import { HttpParams, HttpClient } from "@angular/common/http";
import { NgProgressRef } from "@ngx-progressbar/core";
import { HttpService } from "./http.service";
import { BehaviorSubject } from "rxjs";
import { RoyaltyButtonService } from "@royalty/button";

export enum ResponeTypeEnum {
	json = "json",
	blob = "blob"
}

export enum ResponeContentType {
	xlsx = "application/xlsx"
}

export interface LoadingState {
	target: Function;
	targetName: string;
	isLoading: boolean;
	/** Indicate whenever another request is pending */
	isHavePendingRequest: boolean;
	of: (funcs: Array<Function>) => boolean;
}

export interface IHttpService {
	loadingState$: BehaviorSubject<LoadingState>;
	pendingRequest: number;
	httpClient: HttpClient;
	progressRef: NgProgressRef;
	royaltyButtonService: RoyaltyButtonService;
	getBaseUrl: (adapterSetting: IAdapterSetting) => IAdapterSetting;
	getDefaultHeaders: Function;
	responseInterceptor: Function;
	getSetting: (adapterSetting: IAdapterSetting) => IAdapterSetting;
}

export interface Respone<T> {
	success: boolean;
	code: number;
	content?: T;
	message?: string;
}

export interface DataRespone<T> {
	data: T;
}

export interface IAdapterSetting {
	/**
	 * Version for api connect
	 */
	version?: string;
	/**
	 * Config for routing api url
	 */
	isAdmin?: boolean;
	/**
	 * If authen then no need version
	 */
	isAuthorize?: boolean;
	/**
	 * Auto display respone message if any, default = true
	 */
	showResponeMessage?: boolean;
	/**
	 * Overwrite message when updated successfully if set
	 */
	messageUpdateSuccess?: string;
	/**
	 * Show progress when sending a request, default = true
	 */
	showProgress?: boolean;
	/**
	 * Return data as full respone or just content, default = full
	 */
	data?: "full" | "content" | string;

	/**
	 * Respone type of api, default = json
	 */
	responeType?: ResponeTypeEnum;
	/**
	 * Method request of api
	 */
	method?:
		| "DELETE"
		| "GET"
		| "HEAD"
		| "JSONP"
		| "OPTIONS"
		| "PATCH"
		| "POST"
		| "PUT";

	/**
	 * Ignore api when dont need
	 */
	ignore401?: boolean;
	isReturnResponse?: boolean;
}

export interface DataBlob {
	blob: Blob;
	contentDisposition: string;
	contentType: ResponeContentType;
}

interface ParamObject {
	key: string;
	parameterIndex: number;
}

export function methodBuilder(
	method:
		| "DELETE"
		| "GET"
		| "HEAD"
		| "JSONP"
		| "OPTIONS"
		| "PATCH"
		| "POST"
		| "PUT"
) {
	return function(url: string): MethodDecorator {
		return function(
			target: HttpService,
			propertyKey: string,
			descriptor: any
		) {
			let pPath = target[`${propertyKey}_Path_parameters`];
			let pQuery = target[`${propertyKey}_Query_parameters`];
			let pBody = target[`${propertyKey}_Body_parameters`];

			descriptor.value = function(...args: any[]) {
				let _this = this as IHttpService;
				let _body = createBody(pBody, args);
				let _resUrl = createPath(pPath, url, args);
				let _params = createParams(pQuery, args);
				let _headers = _this.getDefaultHeaders();
				let _targetUrl = `${_this.getBaseUrl(
					descriptor.adapterSetting
				)}${_resUrl}${pQuery ? "/" : ""}`;
				let _settting = _this.getSetting(descriptor.adapterSetting);
				_settting.method = method;

				_this.royaltyButtonService.show();

				// make the request and store the observable for later transformation
				if (_settting.showProgress) {
					_this.progressRef.start();
				}

				_this.pendingRequest++;
				_this[propertyKey]["loading"] = true;
				_this.loadingState$.next(
					createLoadingState({
						targetFunc: target[propertyKey],
						targetName: propertyKey
					})
				);

				let observable = _this.httpClient.request(method, _targetUrl, {
					body: _body,
					headers: _headers,
					params: _params,
					reportProgress: false,
					observe: "response", // only observe http response result
					responseType: _settting.responeType,
					withCredentials: true
				});

				// intercept the response
				observable = _this.responseInterceptor(
					observable,
					_settting,
					propertyKey,
					descriptor.adapter
				);

				return observable;
			};

			return descriptor;
		};
	};
}

export function paramBuilder(paramName: string) {
	return function(_key: string): ParameterDecorator {
		return function(
			_target: HttpService,
			_propertyKey: string,
			_parameterIndex: number
		) {
			let metadataKey = `${_propertyKey}_${paramName}_parameters`;
			let paramObj: ParamObject = {
				key: _key,
				parameterIndex: _parameterIndex
			};

			if (Array.isArray(_target[metadataKey]))
				_target[metadataKey].push(paramObj);
			else _target[metadataKey] = [paramObj];
		};
	};
}

function createBody(pBody: Array<ParamObject>, args: Array<any>): any {
	return pBody ? args[pBody[0].parameterIndex] : null;
}

function createPath(
	pPath: Array<ParamObject>,
	url: string,
	args: Array<any>
): string {
	let resUrl: string = url;

	if (pPath) {
		for (let k in pPath) {
			if (pPath.hasOwnProperty(k)) {
				resUrl = resUrl.replace(
					`{${pPath[k].key}}`,
					args[pPath[k].parameterIndex]
				);
			}
		}
	}

	return resUrl;
}

function createParams(
	pQuery: Array<ParamObject>,
	args: Array<any>
): HttpParams {
	let params = new HttpParams();

	if (pQuery) {
		pQuery
			.filter(p => args[p.parameterIndex]) // filter out optional parameters
			.forEach(p => {
				let key = p.key;
				let value = args[p.parameterIndex];
				// if the value is a instance of Object, we stringify it
				if (value instanceof Object) {
					value = JSON.stringify(value);
				}
				params = params.append(key, value);
			});
	}

	return params;
}

export function filterTargetFunc() {
	let funcs = arguments[0] as Array<Function>;
	let _this = this as LoadingState;
	let result = false;

	for (let idx = 0, length = funcs.length; idx < length; idx++) {
		if (funcs[idx]["loading"]) {
			_this.isHavePendingRequest = true;
		}
		if (funcs[idx] === this.target) {
			_this.isLoading = funcs[idx]["loading"];
			result = true;
		}
	}
	return result;
}

export function createLoadingState(param: {
	targetFunc: Function;
	targetName: string;
}): LoadingState {
	return {
		target: param.targetFunc,
		targetName: param.targetName,
		isLoading: false,
		isHavePendingRequest: false,
		of: filterTargetFunc
	};
}
